import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Badge, MISSION_STATUS, fmtDate, eur } from "../../lib/helpers";
import Card from "../../components/Card";
import HeadStat from "../../components/HeadStat";
import { MissionPanel } from "../../components/MissionPanel";
import KpiSummary from "../../components/KpiSummary";
import MetiersInput from "../../components/MetiersInput";
import RecordShell from "../../components/RecordShell";
import { Modal } from "../../components/ui-modal";
import { PageSkeleton, EmptyState } from "../../components/Feedback";
import { toast } from "sonner";
import AuthImg from "../../components/AuthImg";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../../lib/icons";

const MissionCard = ({ m, onClick }) => {
  // « validee » n'a jamais été un statut calculé de mission : terminée ou annulée = plus urgente.
  const urgent = m.days_left != null && m.days_left <= 2 && !["terminee", "annulee"].includes(m.status);
  return (
    <button onClick={onClick} data-testid={`fiche-mission-${m.id}`}
      className="text-left bg-sable rounded-sm border border-marine/10 p-4 hover:border-marine/40 card-lift">
      <div className="flex items-center justify-between mb-2"><Badge status={m.status} map={MISSION_STATUS} /></div>
      <div className="font-display text-base text-marine">{m.type}</div>
      <div className="text-sm text-marine/80">{m.client_name}</div>
      <div className={`text-xs mt-2 ${urgent ? "text-rouge-texte" : "text-marine/80"}`}>Deadline : {fmtDate(m.deadline)}</div>
    </button>
  );
};

const socialUrl = (platform, val) => {
  if (!val) return null;
  const v = val.trim();
  if (/^https?:\/\//i.test(v)) return v;
  const h = v.replace(/^@/, "");
  const base = { instagram: "https://instagram.com/", linkedin: "https://linkedin.com/in/", facebook: "https://facebook.com/", tiktok: "https://tiktok.com/@", youtube: "https://youtube.com/@" }[platform];
  return base ? base + h : (v.includes(".") ? `https://${v}` : null);
};

// Synthèse des évaluations — une seule grille pour la vue d'ensemble et l'onglet « Évaluations »
// (lot 7, 29/09 : elle était écrite deux fois, avec deux libellés pour la même valeur).
const EvalStatsGrid = ({ s, className = "" }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${className}`}>
    <div className="text-center rounded-sm bg-creme border border-marine/10 p-4"><div className="font-display text-3xl text-ambre-vif flex items-center justify-center gap-1">{s.avg_overall ?? "—"}{s.avg_overall != null && <Icons.Star size={16} className="fill-ambre-vif text-ambre-vif" />}</div><div className="text-xs text-marine/80 mt-1">Note moyenne /5</div></div>
    <div className="text-center rounded-sm bg-creme border border-marine/10 p-4"><div className="font-display text-3xl text-marine">{s.avg_satisfaction ?? "—"}</div><div className="text-xs text-marine/80 mt-1">Satisfaction moyenne /5</div></div>
    <div className="text-center rounded-sm bg-creme border border-marine/10 p-4"><div className="font-display text-3xl text-bordeaux">{s.count}</div><div className="text-xs text-marine/80 mt-1">Missions évaluées</div></div>
  </div>
);

export default function AdminCollaboratorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [allowed, setAllowed] = useState([]);
  const [allowedFiles, setAllowedFiles] = useState({});
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filesByClient, setFilesByClient] = useState({});
  const [expanded, setExpanded] = useState(null);
  const avatarRef = useRef();
  const [editName, setEditName] = useState(false);
  const [nameVal, setNameVal] = useState("");
  const [editRoles, setEditRoles] = useState(false);
  const [rolesVal, setRolesVal] = useState([]);
  const [contact, setContact] = useState({ email: "", phone: "", address: "", country: "", city: "", travel_radius: "" });
  const [note, setNote] = useState("");
  const [noteDirty, setNoteDirty] = useState(false);
  const [bank, setBank] = useState({ bank_account_holder: "", bank_name: "", bank_iban: "", bank_bic: "", bank_address: "" });
  const [socials, setSocials] = useState({ instagram: "", linkedin: "", facebook: "", tiktok: "", youtube: "", other_label: "", other_value: "" });

  const [loadError, setLoadError] = useState(null);
  const load = useCallback(() => api.get(`/admin/collaborators/${id}`).then((r) => {
    setD(r.data);
    setAllowed(r.data.collaborator.allowed_clients || []);
    setAllowedFiles(r.data.collaborator.allowed_files || {});
    const cc = r.data.collaborator;
    setContact({ email: cc.email || "", phone: cc.phone || "", address: cc.address || "", country: cc.country || "", city: cc.city || "", travel_radius: cc.travel_radius ?? "" });
    setNote(cc.internal_note || ""); setNoteDirty(false);
    setBank({ bank_account_holder: cc.bank_account_holder || "", bank_name: cc.bank_name || "", bank_iban: cc.bank_iban || "", bank_bic: cc.bank_bic || "", bank_address: cc.bank_address || "" });
    const s = cc.socials || {};
    setSocials({ instagram: s.instagram || "", linkedin: s.linkedin || "", facebook: s.facebook || "", tiktok: s.tiktok || "", youtube: s.youtube || "", other_label: s.other_label || "", other_value: s.other_value || "" });
    setOpen((prev) => prev ? [...r.data.en_cours, ...r.data.passees].find((m) => m.id === prev.id) || null : null);
  }).catch((e) => setLoadError(e?.response?.status === 404 ? "Collaborateur introuvable." : "Impossible de charger cette fiche.")), [id]);
  const loadFiles = useCallback(async (cid) => { const r = await api.get(`/admin/clients/${cid}/files`); setFilesByClient((prev) => ({ ...prev, [cid]: r.data })); return r.data; }, []);
  useEffect(() => { load(); api.get("/admin/clients").then((r) => setClients(r.data)); api.get("/admin/projects").then((r) => setProjects(r.data)); }, [id, load]);
  // filesByClient est un cache : cet effet ne tourne qu'au changement de périmètre, pas à chaque fichier reçu.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { (allowed || []).forEach((cid) => { if (!filesByClient[cid]) loadFiles(cid); }); }, [allowed, loadFiles]);
  // Avant : `return null` — page blanche pendant tout le chargement (10 s pour un collaborateur
  // à 63 missions, dette du 25/09) et pour toujours en cas d'erreur.
  if (loadError) return <EmptyState icon="UserX" title={loadError} hint="Revenez à la liste des collaborateurs." />;
  if (!d) return <PageSkeleton />;
  const c = d.collaborator;

  const saveName = async () => { if (!nameVal.trim()) return; await api.put(`/admin/collaborators/${id}`, { name: nameVal.trim() }); toast.success("Nom mis à jour"); setEditName(false); load(); };
  const saveRoles = async () => { if (!rolesVal.length) return toast.error("Ajoutez au moins un métier"); await api.put(`/admin/collaborators/${id}`, { job_roles: rolesVal }); toast.success("Métiers mis à jour"); setEditRoles(false); load(); };
  const saveNote = async () => { try { await api.put(`/admin/collaborators/${id}`, { internal_note: note }); setNoteDirty(false); toast.success("Note interne enregistrée"); } catch { toast.error("Échec de l'enregistrement"); } };
  const saveBank = async () => { try { await api.put(`/admin/collaborators/${id}`, bank); toast.success("Informations bancaires enregistrées"); } catch { toast.error("Échec de l'enregistrement"); } };
  const uploadAvatar = async (e) => { const f = e.target.files[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); try { await api.post(`/admin/users/${id}/avatar`, fd); toast.success("Photo mise à jour"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez."); } finally { e.target.value = ""; } };
  const saveContact = async () => {
    if (!contact.country.trim()) return toast.error("Le pays est obligatoire");
    const payload = { phone: contact.phone, address: contact.address, country: contact.country.trim(), city: contact.city.trim(), travel_radius: contact.travel_radius === "" ? null : parseInt(contact.travel_radius) || 0, socials };
    if (contact.email.trim()) payload.email = contact.email.trim();
    try { await api.put(`/admin/collaborators/${id}`, payload); toast.success("Coordonnées enregistrées"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); }
  };
  const persist = async (nextAllowed, nextFiles) => {
    const prevA = allowed, prevF = allowedFiles;
    setAllowed(nextAllowed); setAllowedFiles(nextFiles);
    try { await api.put(`/admin/collaborators/${id}`, { allowed_clients: nextAllowed, allowed_files: nextFiles }); toast.success("Autorisations mises à jour"); }
    catch { setAllowed(prevA); setAllowedFiles(prevF); toast.error("Échec de la mise à jour"); }
  };
  const toggleClient = async (cl) => {
    if (allowed.includes(cl.id)) { const nf = { ...allowedFiles }; delete nf[cl.id]; await persist(allowed.filter((x) => x !== cl.id), nf); }
    else { const files = filesByClient[cl.id] || await loadFiles(cl.id); setExpanded(cl.id); await persist([...allowed, cl.id], { ...allowedFiles, [cl.id]: files.map((f) => f.key) }); }
  };
  const toggleFile = async (cid, key) => { const cur = allowedFiles[cid] || []; const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]; await persist(allowed, { ...allowedFiles, [cid]: next }); };
  const setAllFiles = async (cid, on) => { const keys = (filesByClient[cid] || []).map((f) => f.key); await persist(allowed, { ...allowedFiles, [cid]: on ? keys : [] }); };

  const roles = c.job_roles && c.job_roles.length ? c.job_roles : (c.job_role ? [c.job_role] : []);
  const fin = d.finance_summary || {};
  const np = fin.next_payment;
  const late = np && np.status === "en_retard";

  // ---------- HEADER (toujours visible) ----------
  const header = (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="relative shrink-0">
          <div className="w-16 h-16 rounded-full bg-marine text-white flex items-center justify-center font-display text-2xl overflow-hidden">
            {c.avatar_url ? <AuthImg path={c.avatar_url} alt={c.name} className="w-full h-full object-cover" /> : c.name?.[0]}
          </div>
          <button aria-label="Changer la photo du collaborateur" onClick={() => avatarRef.current.click()} data-testid="collab-avatar-upload" className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-bordeaux text-white flex items-center justify-center border-2 border-creme hover:bg-bordeaux-hover transition-colors"><Icons.Camera size={16} /></button>
          <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
        </div>

        <div className="flex-1 min-w-[220px]">
          {editRoles ? (
            <div className="max-w-md mb-1">
              <MetiersInput testid="collab-metiers-edit" value={rolesVal} onChange={setRolesVal} />
              <div className="flex items-center gap-2 mt-2">
                <button onClick={saveRoles} data-testid="collab-metiers-save" className="bg-marine text-white rounded-sm px-3 py-1.5 text-xs hover:bg-marine-hover">Enregistrer les métiers</button>
                <button onClick={() => setEditRoles(false)} className="text-xs text-marine/80">Annuler</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {roles.map((r, i) => <span key={i} data-testid={`collab-metier-${i}`} className="text-[9px] uppercase tracking-[0.15em] font-semibold text-bordeaux bg-bordeaux-50 border border-bordeaux/30 rounded-sm px-2 py-0.5">{r}</span>)}
              <button aria-label="Modifier les métiers" onClick={() => { setRolesVal([...roles]); setEditRoles(true); }} data-testid="collab-metiers-edit-btn" className="text-marine hover:opacity-70"><Icons.Pencil size={16} /></button>
            </div>
          )}
          {editName ? (
            <div className="flex items-center gap-2">
              <input value={nameVal} onChange={(e) => setNameVal(e.target.value)} data-testid="collab-name-input" className="inp font-display text-xl max-w-sm" />
              <button onClick={saveName} data-testid="collab-name-save" className="bg-marine text-white rounded-sm px-3 py-2 text-sm hover:bg-marine-hover">Enregistrer</button>
              <button onClick={() => setEditName(false)} className="text-sm text-marine/80">Annuler</button>
            </div>
          ) : (
            <h1 className="font-display text-2xl text-marine flex items-center gap-2">{c.name}
              <button aria-label="Modifier le nom du collaborateur" onClick={() => { setNameVal(c.name); setEditName(true); }} data-testid="collab-name-edit" className="text-marine hover:opacity-70"><Icons.Pencil size={16} /></button>
            </h1>
          )}
          <div className="text-sm text-marine/80 flex items-center gap-3 flex-wrap mt-0.5">
            <span className="flex items-center gap-1"><Icons.Mail size={16} className="text-marine" /> {c.email}</span>
            {c.city && <span className="flex items-center gap-1"><Icons.MapPin size={16} className="text-marine" /> {[c.city, c.country].filter(Boolean).join(", ")}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <a href={`mailto:${c.email}`} data-testid="collab-contact-btn" className="inline-flex items-center gap-1.5 text-sm border border-marine/30 text-marine rounded-sm px-3 py-2 hover:bg-marine hover:text-white transition-colors">
            <Icons.Mail size={16} /> Contacter
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-y-3 [&>*:last-child:nth-child(odd)]:col-span-2 mt-4 pt-4 border-t border-marine/10 sm:flex sm:items-stretch sm:gap-1 sm:overflow-x-auto no-scrollbar sm:divide-x sm:divide-marine/10">
        <HeadStat label="Missions" value={d.total} />
        <HeadStat label="En cours" value={d.en_cours.length} accent="text-marine" />
        <HeadStat label="Validées" value={d.validees} accent="text-bleu" />
        <HeadStat label="Budget" value={eur(fin.total_eur)} />
        <HeadStat label="Payé" value={eur(fin.paid_eur)} accent="text-bleu" />
        <HeadStat label="Reste" value={eur(fin.reste_a_payer_eur)} accent="text-bordeaux" />
        <HeadStat label="Note /5" value={d.eval_stats?.avg_overall ?? "—"} accent="text-ambre-vif" />
      </div>
    </div>
  );

  // ---------- SECTIONS ----------
  const renderOverview = () => (
    <div className="space-y-6">
      {d.finance_summary && (
        <KpiSummary testid="collab-finance-kpis" items={[
          { testid: "kpi-collab-budget", label: "Budget total des missions", value: eur(fin.total_eur) },
          { testid: "kpi-collab-paid", accent: "text-bleu", label: "Déjà payé", value: eur(fin.paid_eur) },
          { testid: "kpi-collab-reste", accent: "text-bordeaux", label: "Reste à payer", value: eur(fin.reste_a_payer_eur) },
          { testid: "kpi-collab-next", accent: late ? "text-rouge" : undefined, label: "Prochaine échéance", value: np ? eur(np.amount_eur) : "—", sub: np ? (late ? `En retard · ${fmtDate(np.planned_date)}` : fmtDate(np.planned_date)) : "Aucun paiement restant" },
        ]} />
      )}
      {d.eval_stats && (
        <Card>
          <div className="flex items-center gap-2 mb-4"><h2 className="font-display text-xl text-marine">Synthèse évaluations</h2></div>
          <EvalStatsGrid s={d.eval_stats} />
        </Card>
      )}
    </div>
  );

  const renderCoordonnees = () => (
    <Card>
      <div className="flex items-center gap-2 mb-4" data-testid="contact-section"><h2 className="font-display text-xl text-marine">Coordonnées</h2></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-sm">Adresse e-mail<input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" className="inp mt-1" placeholder="prenom@exemple.com" /></label>
        <label className="text-sm">Numéro de téléphone<input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone" className="inp mt-1" placeholder="+971 ..." /></label>
        <label className="text-sm">Adresse postale<input value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} data-testid="contact-address" className="inp mt-1" placeholder="Rue, ville, code postal" /></label>
        <label className="text-sm">Pays <span className="text-rouge">*</span><input value={contact.country} onChange={(e) => setContact({ ...contact, country: e.target.value })} data-testid="contact-country" className="inp mt-1" placeholder="Émirats arabes unis" required /></label>
        <label className="text-sm">Ville<input value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} data-testid="contact-city" className="inp mt-1" placeholder="Dubaï" /></label>
        <label className="text-sm">Rayon de déplacement (km)<input type="number" min="0" value={contact.travel_radius} onChange={(e) => setContact({ ...contact, travel_radius: e.target.value })} data-testid="contact-travel-radius" className="inp mt-1" placeholder="ex. 50" /></label>
      </div>
      <div className="mt-6 pt-5 border-t border-marine/10" data-testid="socials-block">
        <div className="flex items-center gap-2 mb-3"><h3 className="font-display text-lg text-marine">Réseaux sociaux</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[{ key: "instagram", label: "Instagram", icon: "Instagram", ph: "@profil ou lien" }, { key: "linkedin", label: "LinkedIn", icon: "Linkedin", ph: "profil ou lien" }, { key: "facebook", label: "Facebook", icon: "Facebook", ph: "@profil ou lien" }, { key: "tiktok", label: "TikTok", icon: "Music2", ph: "@profil ou lien" }, { key: "youtube", label: "YouTube", icon: "Youtube", ph: "@chaîne ou lien" }].map(({ key, label, icon, ph }) => {
            const Ico = ICONS_BY_NAME[icon] || Icons.Link; const link = socialUrl(key, socials[key]);
            return (
              <label key={key} className="text-sm">
                <span className="flex items-center gap-1.5 mb-1"><Ico size={16} className="text-marine" /> {label}</span>
                <div className="flex items-center gap-1.5">
                  <input value={socials[key]} onChange={(e) => setSocials({ ...socials, [key]: e.target.value })} data-testid={`social-${key}`} className="inp flex-1" placeholder={ph} />
                  {link && <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`social-link-${key}`} title="Ouvrir le profil" className="shrink-0 w-9 h-9 rounded-sm bg-sable-clair text-marine flex items-center justify-center hover:bg-sable transition-colors"><Icons.ExternalLink size={16} /></a>}
                </div>
              </label>
            );
          })}
        </div>
        <div className="mt-4">
          <span className="flex items-center gap-1.5 mb-1 text-sm"><Icons.Link size={16} className="text-marine" /> Autre</span>
          <div className="grid sm:grid-cols-2 gap-4">
            <input value={socials.other_label} onChange={(e) => setSocials({ ...socials, other_label: e.target.value })} data-testid="social-other-label" className="inp" placeholder="Nom de la plateforme (ex. Behance)" />
            <div className="flex items-center gap-1.5">
              <input value={socials.other_value} onChange={(e) => setSocials({ ...socials, other_value: e.target.value })} data-testid="social-other-value" className="inp flex-1" placeholder="Lien ou identifiant" />
              {socialUrl("other", socials.other_value) && <a href={socialUrl("other", socials.other_value)} target="_blank" rel="noopener noreferrer" data-testid="social-link-other" className="shrink-0 w-9 h-9 rounded-sm bg-sable-clair text-marine flex items-center justify-center hover:bg-sable transition-colors"><Icons.ExternalLink size={16} /></a>}
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-4"><button onClick={saveContact} data-testid="contact-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors">Enregistrer les coordonnées</button></div>
    </Card>
  );

  const renderBank = () => (
    <Card>
      <div className="flex items-center gap-2 mb-1" data-testid="bank-section"><Icons.Landmark size={16} className="text-bordeaux" /><h2 className="font-display text-xl text-marine">Informations bancaires</h2></div>
      <p className="text-sm text-marine/80 mb-4">Coordonnées bancaires privées, visibles uniquement par l'agence. Modifiable à tout moment.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-sm">Titulaire du compte<input value={bank.bank_account_holder} onChange={(e) => setBank({ ...bank, bank_account_holder: e.target.value })} data-testid="bank-holder" className="inp mt-1" placeholder="Nom du titulaire" /></label>
        <label className="text-sm">Banque<input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} data-testid="bank-name" className="inp mt-1" placeholder="Nom de la banque" /></label>
        <label className="text-sm">IBAN<input value={bank.bank_iban} onChange={(e) => setBank({ ...bank, bank_iban: e.target.value })} data-testid="bank-iban" className="inp mt-1" placeholder="AE00 0000 0000 0000 0000 000" /></label>
        <label className="text-sm">BIC / SWIFT<input value={bank.bank_bic} onChange={(e) => setBank({ ...bank, bank_bic: e.target.value })} data-testid="bank-bic" className="inp mt-1" placeholder="ABCDAEAA" /></label>
        <label className="text-sm sm:col-span-2">Adresse bancaire<input value={bank.bank_address} onChange={(e) => setBank({ ...bank, bank_address: e.target.value })} data-testid="bank-address" className="inp mt-1" placeholder="Rue, ville, pays" /></label>
      </div>
      <div className="flex justify-end mt-4"><button onClick={saveBank} data-testid="bank-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors">Enregistrer les informations bancaires</button></div>
    </Card>
  );

  const renderMissions = () => (
    <div className="space-y-8" data-testid="collab-missions-view">
      <div>
        <h2 className="font-display text-xl text-marine mb-4">Missions en cours</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {d.en_cours.map((m) => <MissionCard key={m.id} m={m} onClick={() => setOpen(m)} />)}
          {d.en_cours.length === 0 && <p className="text-sm text-marine/80">Aucune mission en cours.</p>}
        </div>
      </div>
      <div>
        <h2 className="font-display text-xl text-marine mb-4">Missions passées</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {d.passees.map((m) => <MissionCard key={m.id} m={m} onClick={() => setOpen(m)} />)}
          {d.passees.length === 0 && <p className="text-sm text-marine/80">Aucune mission passée.</p>}
        </div>
      </div>
    </div>
  );

  const renderEvaluations = () => (
    <Card>
      <div className="flex items-center gap-2 mb-1" data-testid="collab-eval-stats"><h2 className="font-display text-xl text-marine">Évaluations (interne)</h2></div>
      <p className="text-sm text-marine/80 mb-4">Historique des évaluations réalisées par l'agence à la fin des missions.</p>
      {d.eval_stats ? (<>
        <EvalStatsGrid s={d.eval_stats} className="mb-5" />
        {d.eval_stats.history.length > 0 ? (
          <div className="space-y-2">
            {d.eval_stats.history.map((h, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border-t border-marine/5 pt-2" data-testid={`eval-history-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm text-marine truncate">{h.mission_label}{h.project_name ? ` · ${h.project_name}` : ""}</div>
                  {h.comment && <div className="text-xs text-marine/80 italic truncate">« {h.comment} »</div>}
                  <div className="text-xs text-marine/80">{fmtDate(h.submitted_at)}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0 text-ambre-vif text-sm">{h.overall ?? "—"}<Icons.Star size={16} className="fill-ambre-vif text-ambre-vif" /></div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-marine/80">Aucune évaluation pour le moment.</p>}
      </>) : <p className="text-sm text-marine/80">Aucune évaluation pour le moment.</p>}
    </Card>
  );

  const renderNote = () => (
    <Card>
      <div className="flex items-center gap-2 mb-1" data-testid="internal-note-section"><h2 className="font-display text-xl text-marine">Note interne</h2></div>
      <p className="text-sm text-marine/80 mb-4">Notes privées sur ce collaborateur, visibles uniquement par l'agence. Modifiable à tout moment.</p>
      <textarea value={note} onChange={(e) => { setNote(e.target.value); setNoteDirty(true); }} data-testid="internal-note-input" rows={6} placeholder="Ajouter une note (points forts, disponibilités, remarques...)" className="inp w-full resize-y leading-relaxed" />
      <div className="flex justify-end mt-3"><button onClick={saveNote} disabled={!noteDirty} data-testid="internal-note-save" className={`rounded-sm px-5 py-2 text-sm transition-colors ${noteDirty ? "bg-marine text-white hover:bg-marine-hover" : "bg-sable border border-marine/30 text-marine/80 cursor-not-allowed"}`}>Enregistrer la note</button></div>
    </Card>
  );

  const renderAcces = () => (
    <Card>
      <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">Accès aux fiches entreprises</h2></div>
      <p className="text-sm text-marine/80 mb-4">Cochez les entreprises autorisés, puis affinez fichier par fichier. Le collaborateur ne verra (en lecture seule) que les fichiers cochés. Modifiable à tout moment.</p>
      <div className="space-y-2">
        {clients.map((cl) => {
          const on = allowed.includes(cl.id); const files = filesByClient[cl.id] || []; const sel = allowedFiles[cl.id] || []; const isOpen = expanded === cl.id;
          return (
            <div key={cl.id} data-testid={`access-client-${cl.id}`} className={`rounded-sm border transition-colors ${on ? "border-marine/40 bg-creme" : "border-marine/15"}`}>
              <div className="flex items-center justify-between p-3">
                <label className="flex items-center gap-2 cursor-pointer flex-1">
                  <input type="checkbox" checked={on} onChange={() => toggleClient(cl)} data-testid={`access-toggle-${cl.id}`} className="w-4 h-4 accent-marine" />
                  <span className="w-7 h-7 rounded-full bg-marine text-white flex items-center justify-center text-xs font-display">{cl.name?.[0]}</span>
                  <span className="text-sm text-marine">{cl.name}</span>
                </label>
                {on && <button onClick={() => setExpanded(isOpen ? null : cl.id)} data-testid={`access-expand-${cl.id}`} className="text-xs text-marine flex items-center gap-1 hover:underline">Fichiers ({sel.length}/{files.length}) <Icons.ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} /></button>}
              </div>
              {on && isOpen && (
                <div className="px-3 pb-3 border-t border-marine/10 pt-3" data-testid={`access-files-${cl.id}`}>
                  <div className="flex gap-3 mb-2 text-xs"><button onClick={() => setAllFiles(cl.id, true)} className="text-marine hover:underline">Tout cocher</button><button onClick={() => setAllFiles(cl.id, false)} className="text-marine/80 hover:underline">Tout décocher</button></div>
                  {files.length === 0 ? <p className="text-xs text-marine/80">Aucun fichier associé à cette entreprise pour le moment.</p> : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                      {files.map((f) => (
                        <label key={f.key} data-testid={`file-row-${cl.id}`} className="flex items-center gap-2 cursor-pointer text-sm py-1">
                          <input type="checkbox" checked={sel.includes(f.key)} onChange={() => toggleFile(cl.id, f.key)} data-testid={`file-toggle-${cl.id}`} className="w-4 h-4 accent-bordeaux" />
                          <Icons.File size={16} className="text-marine shrink-0" /><span className="text-marine truncate">{f.name}</span>
                          <span className="text-[10px] uppercase tracking-wider text-bordeaux ml-auto shrink-0">{f.category}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {clients.length === 0 && <p className="text-sm text-marine/80">Aucune entreprise enregistré.</p>}
      </div>
      <div className="text-xs text-marine/80 mt-3" data-testid="access-count">{allowed.length} entreprise(s) autorisé(s)</div>
    </Card>
  );

  const sections = [
    { id: "overview", label: "Vue d'ensemble", icon: "LayoutDashboard", render: renderOverview },
    { id: "coordonnees", label: "Coordonnées", icon: "Contact", render: renderCoordonnees },
    { id: "bancaires", label: "Informations bancaires", icon: "Landmark", render: renderBank },
    { id: "missions", label: "Missions", icon: "Clapperboard", render: renderMissions },
    { id: "evaluations", label: "Évaluations", icon: "Star", render: renderEvaluations },
    { id: "notes", label: "Notes internes", icon: "NotebookPen", render: renderNote },
    { id: "acces", label: "Accès entreprises", icon: "ShieldCheck", render: renderAcces },
  ];

  return (
    <div>
      <button onClick={() => nav("/admin/collaborateurs")} className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-3"><Icons.ArrowLeft size={16} className="text-marine" /> Retour</button>
      <RecordShell
        testid="collab-record"
        breadcrumb={[{ label: "Collaborateurs", to: "/admin/collaborateurs" }, { label: c.name }]}
        header={header}
        sections={sections}
      />
      {open && (
        <Modal onClose={() => setOpen(null)} title={open.type} wide>
          <div className="mb-4"><Badge status={open.status} map={MISSION_STATUS} /></div>
          <MissionPanel m={open} clients={clients} projects={projects} onReload={load} onNavigate={nav} />
        </Modal>
      )}
    </div>
  );
}
